import { collection, addDoc, getDocs, doc, getDoc, query, orderBy, limit, startAfter, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

// Upload image to Firebase Storage for blogs
export const uploadBlogImage = async (file, blogId, imageName) => {
  try {
    const imagePath = `blogs/${blogId}/${imageName}`;
    const storageRef = ref(storage, imagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading blog image:', error);
    throw error;
  }
};

// Add a new blog post
export async function addBlogPost(blogData) {
  try {
    // Generate unique blog ID for storage path
    const tempId = Date.now().toString();
    
    // Upload featured image to Storage if provided
    let featuredImageUrl = '';
    if (blogData.featuredImage && blogData.featuredImage instanceof File) {
      const imageName = `featured-image.${blogData.featuredImage.name.split('.').pop()}`;
      featuredImageUrl = await uploadBlogImage(blogData.featuredImage, tempId, imageName);
    }
    
    // Upload content images to Storage if provided
    let contentImageUrls = [];
    if (blogData.contentImages && Array.isArray(blogData.contentImages)) {
      const uploadPromises = blogData.contentImages.map(async (image, index) => {
        if (image instanceof File) {
          const imageName = `content-${index}.${image.name.split('.').pop()}`;
          return await uploadBlogImage(image, tempId, imageName);
        }
        return null;
      });
      
      const results = await Promise.all(uploadPromises);
      contentImageUrls = results.filter(url => url !== null);
    }
    
    const docRef = await addDoc(collection(db, 'blogs'), {
      ...blogData,
      // Store Firebase Storage URLs instead of base64
      featuredImage: featuredImageUrl,
      contentImages: contentImageUrls,
      createdAt: new Date(),
      updatedAt: new Date(),
      views: 0
    });
    
    console.log('Blog post added with ID:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding blog post:', error);
    throw new Error('Failed to add blog post: ' + error.message);
  }
}

// Get all blog posts with pagination
export async function getBlogPosts(limitCount = 6, lastDoc = null, isAdmin = false) {
  try {
    let constraints = [
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    ];

    // Only filter by published status for non-admin users
    if (!isAdmin) {
      constraints.unshift(where('status', '==', 'published'));
    }

    let q = query(
      collection(db, 'blogs'),
      ...constraints
    );

    if (lastDoc) {
      q = query(
        collection(db, 'blogs'),
        ...constraints,
        startAfter(lastDoc)
      );
    }

    const querySnapshot = await getDocs(q);
    const blogs = [];
    
    querySnapshot.forEach((doc) => {
      blogs.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      blogs,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
      hasMore: querySnapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting blog posts:', error);
    throw new Error('Failed to fetch blog posts: ' + error.message);
  }
}

// Get blog posts by category
export async function getBlogsByCategory(category, limitCount = 6) {
  try {
    const q = query(
      collection(db, 'blogs'),
      where('status', '==', 'published'),
      where('category', '==', category),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const blogs = [];
    
    querySnapshot.forEach((doc) => {
      blogs.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return blogs;
  } catch (error) {
    console.error('Error getting blogs by category:', error);
    throw new Error('Failed to fetch blogs by category: ' + error.message);
  }
}

// Get a single blog post by ID
export async function getBlogPost(blogId) {
  try {
    const docRef = doc(db, 'blogs', blogId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Blog post not found');
    }
  } catch (error) {
    console.error('Error getting blog post:', error);
    throw new Error('Failed to fetch blog post: ' + error.message);
  }
}

// Get related/similar blog posts
export async function getSimilarBlogs(currentBlogId, category, limitCount = 3) {
  try {
    const q = query(
      collection(db, 'blogs'),
      where('status', '==', 'published'),
      where('category', '==', category),
      orderBy('createdAt', 'desc'),
      limit(limitCount + 1) // Get one extra to filter out current blog
    );

    const querySnapshot = await getDocs(q);
    const blogs = [];
    
    querySnapshot.forEach((doc) => {
      if (doc.id !== currentBlogId) {
        blogs.push({
          id: doc.id,
          ...doc.data()
        });
      }
    });

    return blogs.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting similar blogs:', error);
    throw new Error('Failed to fetch similar blogs: ' + error.message);
  }
}

// Search blog posts
export async function searchBlogs(searchTerm) {
  try {
    // Note: Firestore doesn't have full-text search, so this is a basic implementation
    // For production, consider using Algolia or similar service
    const q = query(
      collection(db, 'blogs'),
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const blogs = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const searchText = searchTerm.toLowerCase();
      
      if (
        data.title.toLowerCase().includes(searchText) ||
        data.summary.toLowerCase().includes(searchText) ||
        data.content.toLowerCase().includes(searchText) ||
        (data.tags && data.tags.some(tag => tag.toLowerCase().includes(searchText)))
      ) {
        blogs.push({
          id: doc.id,
          ...data
        });
      }
    });

    return blogs;
  } catch (error) {
    console.error('Error searching blogs:', error);
    throw new Error('Failed to search blogs: ' + error.message);
  }
}

// Get all categories
export async function getBlogCategories() {
  try {
    const q = query(
      collection(db, 'blogs'),
      where('status', '==', 'published')
    );

    const querySnapshot = await getDocs(q);
    const categories = new Set();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.category) {
        categories.add(data.category);
      }
    });

    return Array.from(categories);
  } catch (error) {
    console.error('Error getting blog categories:', error);
    throw new Error('Failed to fetch blog categories: ' + error.message);
  }
}